import React from "react";
import { Link } from "react-router-dom";
import { Logo, ClickableAddress } from "../ui";
import { useLocalizedUrl } from "../../hooks/useLocalizedUrl";
import type { Component, NonNullableField } from "../../content/schema-types";
import * as LucideIcons from "lucide-react";

export type FooterProps = NonNullableField<Extract<Component, { type: "footer" }>, "content">;

type FooterLinkProps = {
    // Optional, weil ctaSchema (content/cta.ts:5) href optional laesst — ein CTA ohne
    // Ziel ist dort legitim (Button.tsx oeffnet damit ein Video statt zu navigieren).
    // Im Footer gibt es diesen Fall nicht: ein Link ohne Ziel ist ein Content-Fehler.
    href?: string;
    className?: string;
    children: React.ReactNode;
};

/**
 * Footer-Link, der sein Ziel durch useLocalizedUrl schickt.
 *
 * Eigene Komponente statt eines Hook-Aufrufs im map()-Callback: Die Rules of Hooks
 * verbieten Letzteres, und die Listen hier haben variable Laenge. Vorbild: Button.tsx:30.
 *
 * Ohne href wird nichts gerendert — lieber ein fehlender Eintrag als ein Link, der wie
 * einer aussieht und nirgendwohin fuehrt. Frueher schluckte `<Link to={undefined}>` das
 * klaglos; sichtbar wurde es erst, als diese Komponente den Typ benannte.
 */
const FooterLink: React.FC<FooterLinkProps> = ({ href, className, children }) => {
    // Vor dem fruehen Ausstieg: die Rules of Hooks verlangen eine konstante Zahl von
    // Hook-Aufrufen pro Render. Im href-losen Fall ist das Ergebnis egal.
    const localizedHref = useLocalizedUrl(href ?? "#");

    if (!href) {
        return null;
    }

    return (
        <Link to={localizedHref} className={className}>
            {children}
        </Link>
    );
};

export const Footer: React.FC<FooterProps> = ({ company, quickLinks, columns, legal, branches, useGradient }) => {
    // Auf /en muss das Logo nach /en/ zeigen, nicht auf die deutsche Startseite.
    // Vorbild: HeaderLogo.tsx:13.
    const homeUrl = useLocalizedUrl("/");

    const getIcon = (iconName?: string) => {
        if (!iconName) {
            return null;
        }

        const IconComponent = LucideIcons[iconName as keyof typeof LucideIcons] as React.ComponentType<{
            className?: string;
        }>;

        if (!IconComponent) {
            return null;
        }

        return <IconComponent className="w-5 h-5" />;
    };

    // Replace any year in copyright text with current year
    const currentYear = new Date().getFullYear();
    const copyrightText = legal.copyright?.replace(/\d{4}/, currentYear.toString()) || "";

    return (
        <footer className="text-white">
            {/* Branches Section with Gradient Background */}
            {branches && branches.items && branches.items.length > 0 && (
                <div className={useGradient ? "bg-gradient py-16 px-4" : "bg-primary-dark py-16 px-4"}>
                    <div className="container mx-auto">
                        <h3 className="text-2xl md:text-3xl font-bold text-center mb-12 text-white">
                            {branches.title}
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 md:gap-8">
                            {branches.items.map((item, index) => (
                                <FooterLink
                                    key={index}
                                    href={item.href}
                                    className="flex items-center gap-3 text-white/90 hover:text-white transition-colors group"
                                >
                                    {getIcon(item.icon)}
                                    <span className="text-sm md:text-base">{item.text}</span>
                                </FooterLink>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Main Footer */}
            <div className="bg-[var(--color-background-black)] py-12">
                <div className="mx-auto w-full px-4 md:px-12">
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-[1.5fr_repeat(auto-fit,minmax(0,1fr))] gap-8 mb-8">
                        <div className="col-span-1 md:col-span-2 lg:col-span-1 lg:pr-8 lg:border-r lg:border-white/20">
                            <div className="mb-6">
                                <Link to={homeUrl} aria-label="Quatico Home">
                                    <Logo variant="light" />
                                </Link>
                            </div>
                            <div className="text-sm text-white/70 space-y-3">
                                <div>
                                    <p className="font-medium text-white mb-2">{company.name}</p>
                                    <ClickableAddress
                                        address={{
                                            street: company.address.street,
                                            city: company.address.city,
                                            country: "Switzerland",
                                        }}
                                        variant="block"
                                        className="text-white/70 hover:text-white"
                                        showIcon={false}
                                    >
                                        <div>
                                            <p>{company.address.street}</p>
                                            <p>{company.address.city}</p>
                                        </div>
                                    </ClickableAddress>
                                </div>
                                {}
                                <a
                                    href={`tel:${company.phone.replace(/\s/g, "")}`}
                                    className="flex items-center gap-2 hover:text-white transition-colors"
                                >
                                    <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                        {/* eslint-disable-next-line max-len */}
                                        <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                                    </svg>
                                    {company.phone}
                                </a>
                                <a
                                    href={`mailto:${company.email}`}
                                    className="flex items-center gap-2 hover:text-white transition-colors"
                                >
                                    <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                                    </svg>
                                    {company.email}
                                </a>
                            </div>
                        </div>

                        <div>
                            <ul className="space-y-2 text-sm text-white/70">
                                {quickLinks.map(link => (
                                    <li key={link.href}>
                                        <FooterLink
                                            href={link.href}
                                            className="hover:text-white transition-colors flex items-center gap-2"
                                        >
                                            <span className="text-white">•</span> {link.text}
                                        </FooterLink>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        {columns.map(column => (
                            <div key={column.title}>
                                <div className="font-bold mb-4">{column.title}</div>
                                <ul className="space-y-2 text-sm text-white/70">
                                    {column.items.map(item => (
                                        <li key={item.href}>
                                            <FooterLink href={item.href} className="hover:text-white transition-colors">
                                                {item.text}
                                            </FooterLink>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>

                    <div className="border-t border-white/20 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
                        <p className="text-sm text-white/60">{copyrightText}</p>
                        <div className="flex flex-col md:flex-row items-center gap-6 text-sm text-white/60">
                            {legal.links.map(link => (
                                <FooterLink
                                    key={link.href}
                                    href={link.href}
                                    className="hover:text-white transition-colors font-medium text-white"
                                >
                                    {link.text}
                                </FooterLink>
                            ))}
                            <span className="flex items-center gap-2">
                                <svg
                                    className="w-4 h-4"
                                    viewBox="0 0 32 32"
                                    xmlns="http://www.w3.org/2000/svg"
                                    aria-label="Swiss flag"
                                >
                                    <path d="m0 0h32v32h-32z" fill="#f00" />
                                    <path d="m13 6h6v7h7v6h-7v7h-6v-7h-7v-6h7z" fill="var(--color-white)" />
                                </svg>
                                {legal.swissMade}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
